import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { MouseEvent, useCallback, useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { Button } from 'src/core/ui';
import { useReportV2ColumnPrecision } from 'src/hooks/useColumnUserSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption, FormatOptionPill } from 'src/modals/EditTableColumns';
import { DeleteIcon, EditIcon, IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import {
  getCustomColumnDisplayName,
  isCustomColumnName,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getTagDisplayNameFromReportingColumnName,
  isTagReportingColumnName,
  ReportTypesV2,
  tagReportingColumnNameToBadgeContentId,
} from 'src/utils/reportsUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature } from 'src/WebApiController';

import { getIsPercentFlagForReportColumn } from './MetricsInputDroppable.utils';
import { SortableMetricsOptionsListItem } from './MetricsOptionsListItem';
import * as styles from './ReportsTableEditor.css';
import { REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES } from './ReportsTableEditorForm.constants';

/**
 * The sections that represent the Add Columns and Current Columns for the Edit Columns experience
 */
export const MetricsOptionsList = ({
  id,
  titleContentId,
  availableValues,
  optionsContent,
  isValueOptions,
  disabled,
  onEditCustomColumn,
  onDeleteCustomColumn,
  onCreateCustomColumn,
  reportType,
  customHeaderContent,
}: {
  id: string;
  titleContentId?: ContentId;
  availableValues: string[];
  optionsContent: Record<string, ContentId>;
  isValueOptions?: boolean;
  disabled?: boolean;
  onEditCustomColumn?: (columnId: string) => void;
  onDeleteCustomColumn?: (columnId: string) => void;
  onCreateCustomColumn?: (e: MouseEvent) => void;
  reportType: ReportTypesV2;
  customHeaderContent?: React.ReactNode;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  const hasReportV2ColumnFormatFeature = useUserHasFeature(
    Feature.ReportV2ColumnFormat
  );

  const { userDefinedPrecisions, updateNumberPrecisionSetting } =
    useReportV2ColumnPrecision(reportType);

  const personalizationConfigOverrides = useMemo(() => {
    return (
      REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[reportType] ?? {}
    );
  }, [reportType]);

  const renderColumnRowActions = useCallback(
    (columnId: string) => {
      const precision = userDefinedPrecisions?.[columnId];

      const { isNumeric, isDateTime, isInteger, isCurrency } =
        personalizationConfigOverrides[columnId] ?? {};

      const isCustomColumn = isCustomColumnName(columnId);
      const isPercent = getIsPercentFlagForReportColumn({
        columnId,
        personalizationConfigOverrides,
        precision,
      });

      return (
        <Stack
          direction="row"
          gap="s"
          alignItems="center"
          // Avoid the click event from propagating to the parent droppable
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isCustomColumnName(columnId) &&
          onEditCustomColumn &&
          onDeleteCustomColumn ? (
            <Stack direction="row">
              <div className={styles.badge}>
                <Content id={ContentId.Custom} />
              </div>
              <EditIcon
                fill={IconsFill.textPrimary}
                withHoverEffect
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCustomColumn(columnId);
                }}
              />
              <DeleteIcon
                fill={IconsFill.textPrimary}
                withHoverEffect
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteCustomColumn(columnId);
                }}
              />
            </Stack>
          ) : isTagReportingColumnName(columnId) ? (
            <div className={styles.badge}>
              <Content id={tagReportingColumnNameToBadgeContentId(columnId)} />
            </div>
          ) : null}

          {hasReportV2ColumnFormatFeature &&
          (isNumeric || isDateTime || isCustomColumn) ? (
            <FormatOptionPill
              id={columnId}
              onSave={updateNumberPrecisionSetting}
              option={precision ?? (isInteger ? FormatOption.Whole : undefined)}
              flags={{
                isDateTime,
                isCurrency,
                isPercent,
                isCustomColumn,
              }}
              isSelectable={true}
              isSelected
              isCompact
              // This prop is not important
              sectionType={SectionType.ListingsReport}
            />
          ) : null}
        </Stack>
      );
    },
    [
      hasReportV2ColumnFormatFeature,
      onDeleteCustomColumn,
      onEditCustomColumn,
      personalizationConfigOverrides,
      updateNumberPrecisionSetting,
      userDefinedPrecisions,
    ]
  );

  return (
    <SortableContext items={availableValues}>
      <Stack
        className={styles.metricsOptionsListContainer}
        gap="s"
        direction="column"
      >
        {titleContentId && <Content id={titleContentId} />}
        {customHeaderContent && customHeaderContent}
        <Stack
          direction="column"
          height="full"
          width="full"
          ref={setNodeRef}
          gap="s"
        >
          {availableValues.map((value) => (
            <SortableMetricsOptionsListItem
              key={value}
              id={value}
              disabled={disabled}
              actionButton={renderColumnRowActions(value)}
            >
              {isCustomColumnName(value) ? (
                <span>{getCustomColumnDisplayName(value)}</span>
              ) : isTagReportingColumnName(value) ? (
                <span>{getTagDisplayNameFromReportingColumnName(value)}</span>
              ) : (
                <Content id={optionsContent[value]} />
              )}
            </SortableMetricsOptionsListItem>
          ))}

          {onCreateCustomColumn && (
            <Button
              variant="text"
              style={{ width: 'fit-content', padding: vars.spacing.sm }}
              onClick={onCreateCustomColumn}
            >
              <PlusIcon size={vars.iconSize.s} fill={IconsFill.textBrand} />
              <Content id={ContentId.AddCustomColumn} />
            </Button>
          )}

          {/* useDroppable container don't have a regular padding. Using placeholder as a hack */}
          <div
            id="placeholder"
            style={{ width: '100%', paddingBottom: vars.spacing.m }}
          ></div>
        </Stack>
      </Stack>
    </SortableContext>
  );
};
